import React, { useState, useEffect } from 'react';
import { X, ClipboardList, Info, Euro, Users, ArrowLeftRight, MessageSquare, AlertCircle, Loader2, Building2, AlertTriangle } from 'lucide-react';
import { Transaction } from '../../types/dashboard';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useEmailNotifications } from '../../hooks/useEmailNotifications';
import { getDistributionNegotiatorIds, parseDistributionKey } from '../../utils/retrocessions';

interface RegulModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
}

export default function RegulModal({ transaction, isOpen, onClose, onSubmit }: RegulModalProps) {
  const { user } = useAuth();
  const { sendRegularizationNotification } = useEmailNotifications();
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [retrocessionsWithDetails, setRetrocessionsWithDetails] = useState<any[]>([]);

  useEffect(() => {
    if (transaction?.id) {
      loadRetrocessionDetails();
    }
  }, [transaction]);

  const loadRetrocessionDetails = async () => {
    if (!transaction?.id) return;

    try {
      const { data: retrocessionsData, error: retrocessionsError } = await supabase
        .from('transaction_retrocessions')
        .select(`
          *,
          primary_negotiator:negotiators!primary_negotiator_id(id, firstname, lastname),
          secondary_negotiator:negotiators!secondary_negotiator_id(id, firstname, lastname),
          tertiary_negotiator:negotiators!tertiary_negotiator_id(id, firstname, lastname),
          quaternary_negotiator:negotiators!quaternary_negotiator_id(id, firstname, lastname)
        `)
        .eq('transaction_id', transaction.id);

      if (retrocessionsError) throw retrocessionsError;

      // Pour chaque rétrocession avec distribution personnalisée, charger les négociateurs
      const enrichedRetrocessions = await Promise.all(retrocessionsData.map(async (retrocession) => {
        if (retrocession.distribution_percentages) {
          const negotiatorIds = getDistributionNegotiatorIds(retrocession.distribution_percentages);

          const { data: negotiatorsData } = await supabase
            .from('negotiators')
            .select('id, firstname, lastname')
            .in('id', negotiatorIds);

          const negotiatorsMap = (negotiatorsData || []).reduce((acc, n) => {
            acc[n.id] = n;
            return acc;
          }, {});

          return {
            ...retrocession,
            negotiators_map: negotiatorsMap
          };
        }
        return retrocession;
      }));

      setRetrocessionsWithDetails(enrichedRetrocessions);
    } catch (err) {
      console.error('Error loading retrocession details:', err);
      setError('Erreur lors du chargement des détails des rétrocessions');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction || !user) return;

    setLoading(true);
    setError('');

    try {
      if (!comment.trim()) {
        throw new Error('Le commentaire est obligatoire');
      }

      // Créer la demande de régularisation
      const { error: requestError } = await supabase
        .from('regul_requests')
        .insert([{
          transaction_id: transaction.id,
          requested_by: user.id,
          status: 'pending',
          request_comment: comment.trim(),
          original_data: {
            mandate_type: transaction.mandate_type,
            seller: transaction.seller,
            buyer: transaction.buyer,
            sale_price: transaction.sale_price,
            fees_with_tax: transaction.fees_with_tax,
            fees_without_tax: transaction.fees_without_tax,
            agencies: transaction.agencies,
            negotiators: transaction.negotiators,
            assistants: transaction.assistants,
            retrocessions: transaction.retrocessions
          },
          new_data: {
            mandate_type: transaction.mandate_type,
            seller: transaction.seller,
            buyer: transaction.buyer,
            sale_price: transaction.sale_price,
            fees_with_tax: transaction.fees_with_tax,
            fees_without_tax: transaction.fees_without_tax,
            agencies: transaction.agencies,
            negotiators: transaction.negotiators,
            assistants: transaction.assistants,
            retrocessions: transaction.retrocessions
          }
        }]);

      if (requestError) throw requestError;

      // Mettre à jour le statut de régularisation
      const { error: statusError } = await supabase
        .from('transactions')
        .update({ regul_status: 1 })
        .eq('id', transaction.id);

      if (statusError) throw statusError;

      // Envoyer une notification email aux administrateurs
      try {
        await sendRegularizationNotification(transaction.id, comment.trim());
      } catch (emailError) {
        console.error('Error sending regularization notification:', emailError);
        // Ne pas faire échouer la demande si l'email échoue
      }

      onSubmit(comment);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !transaction) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getRetrocessionTypeLabel = (type: string) => {
    switch (type) {
      case 'champagne':
        return 'Champagne (21€)';
      case 'ambassadeur':
        return 'Ambassadeur (150€/200€/350€)';
      case 'diagnostic':
        return 'Diagnostic (Montant libre)';
      case 'recommandation_interne':
        return 'Recommandation interne (20% HT)';
      case 'uff':
        return 'UFF (20% HT)';
      case 'retrocession_lefeuvre':
        return 'Rétrocession Lefeuvre (20% HT)';
      case 'retrocession_newco':
        return 'Rétrocession Newco (20% HT)';
      case 'retrocession_syndic':
        return 'Rétrocession KS (20% HT)';
      case 'duo_coaching':
        return 'Duo Coaching (20% HT)';
      case 'intercabinet':
        return 'Intercabinet (Montant libre)';
      case 'geste_commercial':
        return 'Geste commercial (Montant libre)';
      case 'autre':
        return 'Autre (Montant libre)';
      default:
        return type;
    }
  };

  const getDistributionLabel = (retrocession: any) => {
    const getNegotiatorName = (negotiator: any) => 
      negotiator ? `${negotiator.firstname} ${negotiator.lastname}` : '';

    if (retrocession.distribution_percentages) {
      const lines = [];
      Object.entries(retrocession.distribution_percentages).forEach(([key, value]) => {
        if (key !== 'total') {
          // La clé vaut "id" (ancien format) ou "id|AGENCE"
          const { negotiatorId: id, agency } = parseDistributionKey(key.split('#')[0]);
          let negotiatorName = '';
          
          // Chercher le négociateur dans toutes les colonnes possibles
          if (retrocession.negotiators_map?.[id]) {
            const negotiator = retrocession.negotiators_map[id];
            negotiatorName = `${negotiator.firstname} ${negotiator.lastname}`;
          } else if (retrocession.primary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.primary_negotiator);
          } else if (retrocession.secondary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.secondary_negotiator);
          } else if (retrocession.tertiary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.tertiary_negotiator);
          } else if (retrocession.quaternary_negotiator?.id === id) {
            negotiatorName = getNegotiatorName(retrocession.quaternary_negotiator);
          }

          if (negotiatorName) {
            lines.push(`${negotiatorName}${agency ? ` (${agency})` : ''} (${value}%)`);
          }
        }
      });
      return lines.join('\n');
    } else {
      switch (retrocession.distribution_type) {
        case 'single':
          return `100% ${getNegotiatorName(retrocession.primary_negotiator)}`;
        
        case 'equal': {
          const negotiators = [
            retrocession.primary_negotiator,
            retrocession.secondary_negotiator,
            retrocession.tertiary_negotiator,
            retrocession.quaternary_negotiator
          ].filter(Boolean);
          const percentage = Math.round(100 / negotiators.length);
          return negotiators.map(n => `${getNegotiatorName(n)} (${percentage}%)`).join('\n');
        }
        
        default:
          return 'Non spécifié';
      }
    }
  };

  const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    potentiel: { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400', label: 'Potentiel' },
    compromis: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Compromis' },
    acte:      { bg: 'bg-blue-100',  text: 'text-blue-700',  dot: 'bg-blue-500',  label: 'Acte' },
    encaisse:  { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Encaissé' },
    cloture:   { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500', label: 'Clôturé' },
    annule:    { bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500',   label: 'Annulé' },
  };
  const statusStyle = STATUS_STYLES[transaction.status] ?? STATUS_STYLES.potentiel;

  const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value || '-'}</p>
    </div>
  );

  const SectionHeader = ({ icon: Icon, label, headerCls, iconCls, titleCls }: { icon: any; label: string; headerCls: string; iconCls: string; titleCls: string }) => (
    <div className={`px-5 py-3 border-b flex items-center gap-2.5 ${headerCls}`}>
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconCls}`}>
        <Icon size={14} />
      </div>
      <h3 className={`text-xs font-bold uppercase tracking-wider ${titleCls}`}>{label}</h3>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[200] p-0 sm:p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full sm:max-w-4xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        style={{ maxHeight: 'min(92vh, 100dvh)' }}
      >
        {/* ── Header ── */}
        <div className="flex-none bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <ClipboardList size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-white leading-tight">
                    Demande de régularisation
                  </h2>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                    {statusStyle.label}
                  </span>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">
                  Transaction #{transaction.property_number}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5 space-y-4">

          {/* Bandeau d'information */}
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5">
            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Une demande de régularisation sera transmise aux administrateurs pour examen.
              Décrivez précisément les modifications souhaitées dans le commentaire.
            </p>
          </div>

          {/* Informations de la transaction */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Info} label="Informations de la transaction" headerCls="bg-indigo-50 border-indigo-100" iconCls="bg-indigo-100 text-indigo-600" titleCls="text-indigo-700" />
            <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Type de mandat" value={transaction.mandate_type} />
              <Field label="Statut" value={
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                  {statusStyle.label}
                </span>
              } />
              <Field label="Vendeur" value={transaction.seller} />
              <Field label="Acquéreur" value={transaction.buyer} />
            </div>
          </div>

          {/* Informations financières */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Euro} label="Informations financières" headerCls="bg-teal-50 border-teal-100" iconCls="bg-teal-100 text-teal-600" titleCls="text-teal-700" />
            <div className="p-5 grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Prix de vente</p>
                <p className="text-base font-bold text-slate-900">{formatCurrency(transaction.sale_price)}</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-teal-600 uppercase tracking-wide mb-1">Honoraires TTC</p>
                <p className="text-base font-bold text-teal-800">{formatCurrency(transaction.fees_with_tax)}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Honoraires HT</p>
                <p className="text-base font-bold text-emerald-800">{formatCurrency(transaction.fees_without_tax)}</p>
              </div>
            </div>
          </div>

          {/* Agences & Négociateurs */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={Users} label="Agences et négociateurs" headerCls="bg-blue-50 border-blue-100" iconCls="bg-blue-100 text-blue-600" titleCls="text-blue-700" />
            <div className="p-5 grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Agences</p>
                <div className="space-y-1.5">
                  {transaction.agencies.map((agency, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Building2 size={13} className="text-slate-400 shrink-0" />
                      <span className="text-sm font-semibold text-slate-800">{agency}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Négociateurs</p>
                <div className="space-y-2">
                  {transaction.negotiators.map((n, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{n.name}</span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{n.role} · {n.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Rétrocessions */}
          {retrocessionsWithDetails.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <SectionHeader icon={ArrowLeftRight} label="Rétrocessions" headerCls="bg-rose-50 border-rose-100" iconCls="bg-rose-100 text-rose-600" titleCls="text-rose-700" />
              <div className="p-5 space-y-3">
                {retrocessionsWithDetails.map((r, i) => (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4 grid grid-cols-4 gap-4">
                    <Field label="Type" value={getRetrocessionTypeLabel(r.type)} />
                    <Field label="Montant" value={formatCurrency(r.amount)} />
                    <Field label="Description" value={r.description} />
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Répartition</p>
                      <p className="text-sm font-semibold text-slate-900 whitespace-pre-line">{getDistributionLabel(r)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Commentaire */}
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <SectionHeader icon={MessageSquare} label="Motif de la demande" headerCls="bg-orange-50 border-orange-100" iconCls="bg-orange-100 text-orange-600" titleCls="text-orange-700" />
            <div className="p-5">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder="Expliquez précisément les modifications souhaitées et les raisons de cette demande..."
                required
                className="w-full px-3.5 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 resize-y text-sm text-slate-800 placeholder:text-slate-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex-none border-t border-slate-200 bg-white px-6 py-4 flex items-center gap-4">
          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm mr-auto">
              <AlertCircle size={14} className="shrink-0" />
              <span className="line-clamp-2">{error}</span>
            </div>
          )}
          <div className="flex gap-3 ml-auto shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-slate-700 to-slate-900 rounded-xl hover:from-slate-800 hover:to-black disabled:opacity-60 transition-all shadow-sm"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? 'Envoi en cours…' : 'Envoyer la demande'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}